/* Book detail modal — opens when a book card is clicked.
 * Shows pricing, ISBN, page/word count, sample text, bundle offers,
 * and the "Buy now" CTA. Logged-out users get redirected to /bookstore/login.
 */
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';   // Feb-2026 · i18n Wave 4
import { toast } from 'sonner';
import { X, ShoppingBag, Sparkles, Languages, ShieldCheck, Heart, Award, BookOpen, Loader2 } from 'lucide-react';
import { bookstoreApi } from '@/lib/bookstoreApi';
import { Money, useCurrency } from '@/contexts/CurrencyContext';
import { useBookstoreAuth } from '@/lib/bookstoreAuthContext';
import useCanonicalTags from '@/lib/useCanonicalTags';
import BookDetailPodPane from './BookDetailPodPane';
import BookDetailCover from './BookDetailCover';
import BookDetailBundles from './BookDetailBundles';
import LookInsideReader from './LookInsideReader';
import { taglineFromNavigator } from '@/lib/bookstoreTaglineI18n';

export default function BookDetailModal({ book, onClose }) {
  const { t } = useTranslation();
  const canonicalTags = useCanonicalTags();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useBookstoreAuth();
  const { format: fmtMoney, formatPrice } = useCurrency();
  const [bundles, setBundles] = useState([]);
  const [worldMates, setWorldMates] = useState([]);
  const [buying, setBuying] = useState(false);
  useEffect(() => {
    bookstoreApi.trackStorefrontEvent({ event_type: 'detail_open', book_id: book.id, genre_slug: book.genre_slug });
  }, [book.id, book.genre_slug]);

  // iter-29 — language picker
  const [languages, setLanguages] = useState([]);
  const [selectedLang, setSelectedLang] = useState(book.original_language || 'en');
  const [quote, setQuote] = useState(null);
  const [trans, setTrans] = useState(null);
  const [translating, setTranslating] = useState(false);
  // iter-p3-l — Wishlist state.  Populated from GET /bookstore/wishlist
  // on mount, then optimistically flipped on click.
  const [wishlistLang, setWishlistLang] = useState(null);
  const [wishlistBusy, setWishlistBusy] = useState(false);
  useEffect(() => {
    if (!user) return;
    let alive = true;
    bookstoreApi.wishlist()
      .then((d) => {
        if (!alive) return;
        const row = (d?.wishlist || []).find((w) => w.book_id === book.id);
        setWishlistLang(row ? (row.desired_language || '') : null);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [book.id, user]);
  const toggleWishlist = async () => {
    if (!user) {
      toast.info(t('bookstore.signInToSave'));
      navigate(`/bookstore/login?next=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    setWishlistBusy(true);
    try {
      if (wishlistLang !== null) {
        await bookstoreApi.wishlistRemove(book.id);
        setWishlistLang(null);
        toast.success(t('bookstore.wishlistRemoved'));
      } else {
        const desired = user?.preferred_language || null;
        const r = await bookstoreApi.wishlistAdd(book.id, desired);
        setWishlistLang(r?.wishlist?.desired_language || '');
        toast.success(desired
          ? `${t('bookstore.wishlistSaved')} ${t('bookstore.wishlistWaitLang', { lang: desired.toUpperCase() })}`
          : `${t('bookstore.wishlistSaved')} ${t('bookstore.wishlistWaitAny')}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update wishlist.');
    } finally { setWishlistBusy(false); }
  };

  useEffect(() => {
    let alive = true;
    bookstoreApi.book(book.id).then(({ bundles, world_mates }) => {
      if (alive) { setBundles(bundles || []); setWorldMates(world_mates || []); }
    }).catch(() => {});
    bookstoreApi.languages().then((d) => { if (alive) setLanguages(d.languages || []); }).catch(() => {});
    return () => { alive = false; };
  }, [book.id]);

  useEffect(() => {
    if (!selectedLang) return;
    let alive = true;
    bookstoreApi.translationQuote(book.id, selectedLang).then((q) => { if (alive) setQuote(q); }).catch(() => {});
    const original = (book.original_language || 'en').toLowerCase();
    if (selectedLang === original) {
      if (alive) setTrans(null);
      return;
    }
    setTranslating(true);
    bookstoreApi.translation(book.id, selectedLang)
      .then((t) => { if (alive) setTrans(t); })
      .catch(() => { if (alive) setTrans(null); })
      .finally(() => { if (alive) setTranslating(false); });
    return () => { alive = false; };
  }, [book.id, selectedLang, book.original_language]);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  // iter-p3-c — Set <html lang="…"> so screen readers and SEO crawlers
  // pick up the right locale while the reader is viewing this book.
  // The value is computed by the backend as `html_lang` (primary
  // subtag of the stored `language_code`, defaulting to 'en').
  useEffect(() => {
    const lang = book.html_lang || (book.language_code ? book.language_code.split('-')[0].toLowerCase() : 'en');
    const prev = document.documentElement.getAttribute('lang');
    document.documentElement.setAttribute('lang', lang);
    // RTL scripts: flip direction on the modal body.
    const isRTL = lang === 'ar' || lang === 'he';
    if (isRTL) document.documentElement.setAttribute('dir', 'rtl');
    return () => {
      if (prev) document.documentElement.setAttribute('lang', prev);
      else document.documentElement.removeAttribute('lang');
      document.documentElement.removeAttribute('dir');
    };
  }, [book.html_lang, book.language_code]);

  const startBuy = async () => {
    bookstoreApi.trackStorefrontEvent({ event_type: 'buy_intent', book_id: book.id, genre_slug: book.genre_slug });
    if (!user) {
      toast.info('Sign in to your Book Store account to make a purchase.');
      navigate(`/bookstore/login?next=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    setBuying(true);
    try {
      const { url } = await bookstoreApi.checkoutBook(book.id, window.location.origin);
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout.');
      setBuying(false);
    }
  };

  const startBundle = async (b) => {
    if (!user) {
      toast.info('Sign in to your Book Store account to buy a bundle.');
      navigate(`/bookstore/login?next=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    try {
      const { url } = await bookstoreApi.checkoutBundle(b.id, window.location.origin);
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start bundle checkout.');
    }
  };

  const displayTitle = trans?.title || book.title;
  const displayBackCopy = trans?.back_copy || book.back_copy;
  const displaySampleText = trans?.sample_text || book.sample_text;
  const displayPriceCents = quote?.marked_cents ?? book.price_cents;
  const showMarkup = !!(quote && quote.markup_pct > 0);

  // Look Inside — upcoming books pull chapters from the writing workspace
  const [upcomingLookText, setUpcomingLookText] = useState(null);
  const [lookInsideLoading, setLookInsideLoading] = useState(false);
  const [lookInsideShown, setLookInsideShown] = useState(false);
  const API_PUB = process.env.REACT_APP_BACKEND_URL || '';

  const fetchLookInside = async () => {
    if (upcomingLookText) { setLookInsideShown(true); return; }
    setLookInsideLoading(true);
    try {
      const r = await fetch(`${API_PUB}/api/bookstore/books/${book.id}/look-inside`);
      if (!r.ok) throw new Error('Preview unavailable.');
      const d = await r.json();
      const text = (d.chapters || []).map(ch => ch.content).join('\n\n---\n\n');
      setUpcomingLookText(text || 'Preview not available yet.');
      setLookInsideShown(true);
    } catch (e) {
      toast.error(e.message || 'Preview unavailable.');
    } finally {
      setLookInsideLoading(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid={`book-detail-${book.id}`}
      className="fixed inset-0 z-50 flex items-end bg-black/60 px-3 pb-3 backdrop-blur-sm md:items-center md:justify-center md:p-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full max-w-3xl overflow-hidden rounded-xl border border-border bg-card text-foreground shadow-panel-active md:max-h-[88vh]">
        <div className="flex items-center justify-between border-b border-border bg-panel-header/50 px-5 py-3">
          <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Book details</div>
          <button
            onClick={onClose}
            data-testid="book-detail-close"
            className="rounded-sm p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-[200px_1fr] md:overflow-y-auto md:max-h-[calc(88vh-44px)]">
          <BookDetailCover book={book} />

          <div className="min-w-0">
            <div className="flex items-start gap-2">
              {book.author_slug ? (
                <button
                  type="button"
                  onClick={() => {
                    // Already on this author's page? Just close the modal
                    // instead of a no-op navigation to the same route.
                    const target = `/bookstore/author/${book.author_slug}`;
                    onClose?.();
                    if (location.pathname !== target) navigate(target);
                  }}
                  data-testid={`book-detail-author-link-${book.id}`}
                  className="flex min-w-0 items-start gap-2 text-left"
                >
                  {book.author_portrait_url && (
                    <img
                      src={book.author_portrait_url}
                      alt={book.author}
                      data-testid={`book-detail-author-portrait-${book.id}`}
                      className="h-10 w-10 shrink-0 rounded-full border border-border object-cover"
                    />
                  )}
                  <span className="min-w-0">
                    <span className="block text-[11px] uppercase tracking-[0.18em] text-muted-foreground underline decoration-muted-foreground/40 underline-offset-2 hover:text-foreground hover:decoration-foreground">
                      {book.author}
                    </span>
                    {book.author_bio && (
                      <span className="mt-0.5 line-clamp-2 block text-[11px] font-normal normal-case leading-snug tracking-normal text-muted-foreground/90" data-testid={`book-detail-author-bio-${book.id}`}>
                        {book.author_bio}
                      </span>
                    )}
                  </span>
                </button>
              ) : (
                <div className="min-w-0">
                  {book.author_portrait_url && (
                    <img
                      src={book.author_portrait_url}
                      alt={book.author}
                      data-testid={`book-detail-author-portrait-${book.id}`}
                      className="h-10 w-10 shrink-0 rounded-full border border-border object-cover"
                    />
                  )}
                  <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{book.author}</div>
                  {book.author_bio && (
                    <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-muted-foreground/90" data-testid={`book-detail-author-bio-${book.id}`}>
                      {book.author_bio}
                    </p>
                  )}
                </div>
              )}
            </div>
            <h2 className="mt-1 text-2xl font-semibold leading-tight tracking-tight">{displayTitle}</h2>

            {languages.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-border bg-panel-header/40 px-2 py-1.5">
                <Languages className="h-3.5 w-3.5 text-primary" />
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground">{t('bookstore.language')}</label>
                <select
                  data-testid={`book-language-select-${book.id}`}
                  value={selectedLang}
                  onChange={(e) => setSelectedLang(e.target.value)}
                  className="h-7 rounded-sm border border-border bg-background px-2 text-[12px]"
                >
                  {languages.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.name}{l.markup_pct ? ` (+${l.markup_pct}%)` : ''}
                    </option>
                  ))}
                </select>
                {translating && <span className="text-[10.5px] text-muted-foreground">{t('bookstore.translatingSample')}</span>}
                {showMarkup && (
                  <span className="text-[10.5px] text-muted-foreground">
                    Base <Money usdCents={book.price_cents} /> · markup {quote.markup_pct}%
                  </span>
                )}
              </div>
            )}

            <div className="mt-2 flex items-center gap-2">
              <div className="text-3xl font-semibold tracking-tight text-primary" data-testid={`book-price-${book.id}`}>
                {/* iter-53d — Only apply per-currency overrides when the translation markup is 0
                 *  (i.e. the customer's selected language is the book's original). A non-zero
                 *  markup is a multiplier on the USD base, so we never mix it with a pinned price. */}
                <Money usdCents={
                  user && user.first_purchase_promo_eligible !== false
                    ? Math.round(displayPriceCents * 0.8)
                    : displayPriceCents
                }
                       overrides={(quote?.markup_pct || 0) > 0 ? undefined : book.price_overrides} />
              </div>
              {/* Feb-2026 — First-buy 20% off badge + strikethrough
                * of the list price. Only shown for logged-in customers
                * whose promo flag is still eligible. */}
              {user && user.first_purchase_promo_eligible !== false && (
                <>
                  <div className="text-[14px] text-muted-foreground line-through" data-testid={`book-price-list-${book.id}`}>
                    <Money usdCents={displayPriceCents}
                           overrides={(quote?.markup_pct || 0) > 0 ? undefined : book.price_overrides} />
                  </div>
                  <span
                    data-testid={`first-buy-discount-badge-${book.id}`}
                    className="inline-flex items-center gap-1 rounded-full bg-oxblood px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-white shadow-sm"
                  >
                    First buy · 20% off
                  </span>
                </>
              )}
              {book.is_bestseller && (
                <span className="rounded-sm bg-success/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-success">Best-seller</span>
              )}
              {book.is_upcoming && (
                <span className="rounded-sm bg-warning/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-warning">Pre-order</span>
              )}
              {book.is_spotlight && (
                <span className="inline-flex items-center gap-1 rounded-sm bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary"><Sparkles className="h-2.5 w-2.5" /> Spotlight</span>
              )}
            </div>

            <p className="mt-3 whitespace-pre-line text-[13px] leading-relaxed text-foreground/85">{displayBackCopy}</p>

            {/* OHAS verification badge + QR — every certified book carries
             *  a scannable manifest link so buyers can verify human
             *  authorship before purchase.  */}
            {book.ohas_level && (
              <div
                className="mt-3 flex items-center gap-3 rounded-md border border-purple-500/40 bg-purple-500/5 p-3"
                data-testid={`book-detail-ohas-${book.id}`}
              >
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL || ''}/api/ohas/${book.id}/badge.svg`}
                  alt={`OHAS ${book.ohas_level} badge`}
                  className="h-6"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-[10.5px] uppercase tracking-[0.16em] text-purple-700 dark:text-purple-300">
                    <Award className="mr-1 inline h-3 w-3" />
                    OHAS {book.ohas_level} · human authorship verified
                  </div>
                  <div className="mt-0.5 text-[11.5px] text-muted-foreground">
                    Scan to verify at{' '}
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL || ''}/api/ohas/${book.id}/manifest.json`}
                      target="_blank" rel="noreferrer"
                      className="underline hover:text-primary"
                      data-testid={`book-detail-ohas-verify-${book.id}`}
                    >
                      manifest.json
                    </a>
                  </div>
                </div>
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL || ''}/api/ohas/${book.id}/qr.png`}
                  alt="OHAS verification QR code"
                  className="h-16 w-16 rounded border border-border bg-white p-0.5"
                  data-testid={`book-detail-ohas-qr-${book.id}`}
                />
              </div>
            )}

            {/* Feb-2026 (indie-conv batch2) — Trope + heat chips row. */}
            {(Array.isArray(book.tropes) && book.tropes.length > 0) || Number(book.heat_level || 0) > 0 ? (
              <div className="mt-3 flex flex-wrap items-center gap-1.5"
                   data-testid={`book-detail-tropes-${book.id}`}>
                {Number(book.heat_level || 0) > 0 && (
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-orange-500/20 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-orange-700 dark:text-orange-300"
                        title={`Heat level ${book.heat_level} / 5`}
                        data-testid={`book-detail-heat-${book.id}`}>
                    {'🔥'.repeat(Math.min(5, Number(book.heat_level)))}
                    <span className="ml-1">Heat {book.heat_level}/5</span>
                  </span>
                )}
                {(book.tropes || []).map((t) => (
                  <span
                    key={t}
                    className="rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[10.5px] font-medium uppercase tracking-wider text-primary"
                  >
                    {t}
                  </span>
                ))}
              </div>
            ) : null}

            {/* iter-greyrom — Reader-warning chip row. Rendered under the
                blurb (never behind cover art) so it's always legible.
                iter-romance28 — Canonical tags get a filled shield badge. */}
            {Array.isArray(book.content_tags) && book.content_tags.length > 0 && (
              <div className="mt-3" data-testid={`book-detail-warnings-${book.id}`}>
                <div className="mb-1 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                  Reader warnings
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {book.content_tags.map((t) => {
                    const canon = canonicalTags.isCanonical(t);
                    return (
                      <span
                        key={t}
                        title={canon ? 'Canonical shelf-wide label' : ''}
                        className="inline-flex items-center gap-1 rounded-full border border-oxblood/40 bg-oxblood/10 px-2 py-0.5 text-[10.5px] font-medium uppercase tracking-wider text-oxblood dark:text-destructive/80"
                      >
                        {canon && <ShieldCheck className="h-3 w-3 text-emerald-600 dark:text-emerald-400" strokeWidth={2.5} />}
                        {t}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {displaySampleText && (
              // Feb-2026 (indie-conv batch2 pt3) — Real 3-chapter reader
              // with font picker, dark mode, progress bar, abandonment
              // telemetry. Replaces the collapse-drawer teaser.
              <LookInsideReader
                bookId={book.id}
                text={displaySampleText}
                priceLabel={(quote?.markup_pct || 0) > 0 ? fmtMoney(displayPriceCents) : formatPrice(displayPriceCents, book.price_overrides)}
                onBuy={startBuy}
                buying={buying}
              />
            )}

            {/* Look Inside for upcoming/pre-release books (no sample_text yet) */}
            {book.is_upcoming && !displaySampleText && (
              <div className="mt-4" data-testid={`look-inside-upcoming-${book.id}`}>
                {!lookInsideShown ? (
                  <button
                    type="button"
                    onClick={fetchLookInside}
                    disabled={lookInsideLoading}
                    data-testid={`look-inside-trigger-${book.id}`}
                    className="inline-flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-4 py-2 text-[13px] font-semibold text-primary hover:bg-primary/10 transition-colors disabled:opacity-60"
                  >
                    {lookInsideLoading
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <BookOpen className="h-4 w-4" />}
                    Look Inside · First 3 Chapters (free)
                  </button>
                ) : upcomingLookText ? (
                  <LookInsideReader
                    bookId={book.id}
                    text={upcomingLookText}
                    priceLabel="Coming Soon"
                    onBuy={startBuy}
                    buying={buying}
                    onClose={() => setLookInsideShown(false)}
                  />
                ) : null}
              </div>
            )}

            <div className="mt-5 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={startBuy}
                disabled={buying}
                data-testid={`buy-book-${book.id}`}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-[13px] font-semibold text-primary-foreground shadow-panel-active transition hover:bg-primary/90 disabled:opacity-60"
              >
                <ShoppingBag className="h-4 w-4" />
                {buying ? t('tokens.checkingOut') : `${t('bookstore.buyNow')} · ${(quote?.markup_pct || 0) > 0 ? fmtMoney(displayPriceCents) : formatPrice(displayPriceCents, book.price_overrides)}`}
              </button>
              <button
                type="button"
                onClick={toggleWishlist}
                disabled={wishlistBusy}
                data-testid={`wishlist-toggle-${book.id}`}
                aria-pressed={wishlistLang !== null}
                title={wishlistLang !== null
                  ? t('bookstore.removeFromWishlist')
                  : t('bookstore.addToWishlist')}
                className={`inline-flex items-center gap-2 rounded-md border px-3.5 py-2.5 text-[12.5px] font-semibold transition disabled:opacity-60 ${
                  wishlistLang !== null
                    ? 'border-oxblood bg-oxblood/10 text-oxblood dark:text-destructive/80 hover:bg-oxblood/20'
                    : 'border-border bg-background text-foreground hover:border-oxblood/60 hover:text-oxblood'
                }`}
              >
                <Heart
                  className="h-4 w-4"
                  fill={wishlistLang !== null ? 'currentColor' : 'none'}
                  strokeWidth={2}
                />
                {wishlistLang !== null
                  ? (wishlistLang ? `${t('bookstore.addToWishlist')} · ${wishlistLang.toUpperCase()}` : t('bookstore.addToWishlist'))
                  : t('bookstore.addToWishlist')}
              </button>
            </div>

            {/* iter-50j — POD softcover + optional signed-cover upcharge (extracted into BookDetailPodPane in iter-53o) */}
            <BookDetailPodPane
              book={book}
              user={user}
              onRequireSignIn={() =>
                navigate(`/bookstore/login?next=${encodeURIComponent(window.location.pathname)}`)
              }
            />

            {bundles.length > 0 && (
              <BookDetailBundles bundles={bundles} onBuyBundle={startBundle} />
            )}
            {worldMates.length > 0 && (
              <div className="mt-6 border-t border-oxblood/20 pt-4" data-testid={`book-detail-world-mates-${book.id}`}>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h3 className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">More from this world</h3>
                  {book.world_tag && (
                    <button
                      type="button"
                      onClick={() => { onClose?.(); navigate(`/bookstore/world/${book.world_tag}`); }}
                      data-testid={`book-detail-view-world-${book.id}`}
                      className="shrink-0 text-[10.5px] font-semibold text-primary hover:underline"
                    >
                      View full collection →
                    </button>
                  )}
                </div>
                <div className="flex gap-3 overflow-x-auto pb-1">
                  {worldMates.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => {
                        onClose?.();
                        if (m.author_slug) navigate(`/bookstore/author/${m.author_slug}`);
                        else if (m.genre_slug) navigate(`/bookstore/g/${m.genre_slug}`);
                      }}
                      data-testid={`book-detail-world-mate-${m.id}`}
                      className="group flex w-[88px] shrink-0 flex-col items-start text-left"
                    >
                      {m.cover_front_url && (
                        <img
                          src={m.cover_front_url}
                          alt={m.title}
                          className="aspect-[2/3] w-full rounded-sm border border-border object-cover transition-transform group-hover:scale-[1.03]"
                        />
                      )}
                      <span className="mt-1 line-clamp-2 text-[10.5px] font-medium leading-snug group-hover:underline">{m.title}</span>
                      <span className="text-[9.5px] text-muted-foreground">{m.author}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {/* iter-p3-x — Brand tagline footer, localised via
             *  navigator.language to match the storefront hero. */}
            <p
              className="mt-6 border-t border-oxblood/20 pt-4 text-center text-[13px] italic text-oxblood/80 dark:text-destructive/70"
              data-testid={`book-detail-tagline-${book.id}`}
            >
              {taglineFromNavigator()}
            </p>

            {/* Feb-2026 (indie-conv batch2) — Reader → Writer cross-sell CTA.
             * Cross-pollinates bookstore visitors into paid Loom trial signups. */}
            <div
              className="mt-4 overflow-hidden rounded-lg border border-primary/40 bg-gradient-to-br from-primary/10 via-card to-card p-4"
              data-testid={`book-detail-writer-cta-${book.id}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-[10.5px] uppercase tracking-[0.22em] text-primary">
                    From reader to writer
                  </div>
                  <div className="mt-1 text-[14.5px] font-semibold tracking-tight text-foreground">
                    Love this? Write your own — free to try.
                  </div>
                  <p className="mt-1 text-[12px] text-muted-foreground">
                    The Loom is our AI-assisted writing suite behind every book on this shelf.
                    Free to get started — no card required.
                  </p>
                </div>
                <a
                  href="/"
                  data-testid={`book-detail-writer-cta-link-${book.id}`}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-[12.5px] font-semibold text-primary-foreground shadow transition hover:bg-primary/90"
                >
                  <Sparkles className="h-3.5 w-3.5" /> Begin writing — free to try
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
